import { action, flow, makeObservable, observable } from 'mobx';

import { agent } from 'src/api/agent';
import { BaseApiError, OperationAbortedError } from 'src/errors';

import type { NotificationsStore } from '../notifications-store/notifications-store';
import type { PlanVersionsService } from '../plan-version/plan-versions-service';

import type { EXPORT_TAGS } from './consts';
import { ExportServiceApi } from './export-service.api';

export type TExportData = {
  startDate: number;
  endDate: number | null;
  exportTags: EXPORT_TAGS[];
};

export class ExportService {
  private readonly api: ExportServiceApi;
  private readonly notifications: NotificationsStore;
  private readonly planVersionService: PlanVersionsService;
  @observable file: File | null = null;
  @observable error: string | null = null;
  @observable private abortController: AbortController | null = null;
  @observable isExporting = false;

  constructor(notifications: NotificationsStore, planVersionService: PlanVersionsService) {
    this.notifications = notifications;
    this.planVersionService = planVersionService;
    this.api = new ExportServiceApi(agent);

    makeObservable(this);
  }

  @action.bound
  cancelImport() {
    this.abortController?.abort();
    this.abortController = null;
  }

  @action.bound
  removeFile(): void {
    this.file = null;
  }

  @flow.bound
  async *exportRequest(
    data: TExportData & {
      ganttPaletteSections?: string[];
    },
  ) {
    const planVersion = this.planVersionService.currentVersion;

    if (!planVersion) {
      console.error('planVersion is not presented');
      return;
    }

    this.error = null;
    this.isExporting = true;
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    const planVersionName = planVersion.data.name;

    try {
      const file = await this.api.export(planVersion.id, data, planVersionName, abortController.signal);

      this.abortController = null;
      this.file = file;
    } catch (e) {
      yield;

      if (e instanceof OperationAbortedError) {
        return;
      }

      this.abortController = null;

      if (e instanceof BaseApiError && (e.responseMessage || e.message)) {
        this.error = e.responseMessage || e.message;
        this.notifications.showErrorMessage(e.responseMessage || e.message);
        return;
      }

      console.error(e);
      this.error = 'errors:failedToExportData';
      this.notifications.showErrorMessageT('errors:failedToExportData');
      return;
    } finally {
      yield;
      this.isExporting = false;
    }
  }
}
